import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ChevronLeft, ChevronRight, Star } from 'lucide-react'
import { ExportButton } from '@/components/ui/ExportButton'
import { apiErrorText } from '@/lib/api-error'
import { fetchWeek, updateAssignment, weekExportRequest } from '../../api/work-cockpit.api'
import type { CockpitWeek as Woche, WeekTile } from '../../types/cockpit.types'
import { formatStunden, heuteIso, kurzDatum, plusTage } from './cockpit-labels'
import { bestaetige } from '@/lib/dialoge'

type Gezogen =
  | { art: 'kachel' | 'rand'; assignmentId: string; userId: string; startsOn: string; endsOn: string; vonTag: string }
  | { art: 'karte'; stepId: string }

/**
 * AP-35 Detailblatt 11 4.5 — **die Sicht Woche.**
 *
 * Matrix Mitarbeiter × Tage, phasenübergreifend (12.2-4): Die Phase des Reiters
 * ist hervorgehoben, die anderen gedämpft. Abwesenheiten aus AP-19 grau;
 * Einteilung auf einem Abwesenheitstag rot. Ziehen: Kachel auf einen anderen
 * Tag verschiebt den Zeitraum, auf eine andere Zeile wechselt den Mitarbeiter
 * (mit Bestätigung), der Rand verlängert; eine Karte aus dem Streifen „nicht
 * eingeteilt" in eine Zelle öffnet den Dialog mit Mitarbeiter und Tag.
 */
export function CockpitWeek({
  phaseKey,
  darfEinteilen,
  version,
  onEinteilen,
  onOpenStep,
}: {
  phaseKey: string
  darfEinteilen: boolean
  version: number
  onEinteilen: (stepIds: string[], vorbelegt?: { userId?: string; startsOn?: string; endsOn?: string }) => void
  onOpenStep: (stepId: string) => void
}) {
  const { t } = useTranslation()
  const [tag, setTag] = useState(heuteIso())
  const [nurPhase, setNurPhase] = useState(false)
  const [wochenende, setWochenende] = useState(false)
  const [aushang, setAushang] = useState('')
  const [woche, setWoche] = useState<Woche | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [gezogen, setGezogen] = useState<Gezogen | null>(null)
  const [ziel, setZiel] = useState<string | null>(null)

  const laden = useCallback(async () => {
    setFehler(null)
    try {
      setWoche(await fetchWeek({ from: tag, phaseKey, onlyPhase: nurPhase || undefined }))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Woche konnte nicht geladen werden.'))
    }
  }, [tag, phaseKey, nurPhase])

  useEffect(() => {
    void laden()
  }, [laden, version])

  const tage = (woche?.days ?? []).filter((d) => wochenende || d.weekday < 5 || woche?.rows.some((r) => r.days.find((c) => c.date === d.date)?.tiles.length))

  async function ablegen(userId: string, datum: string) {
    const g = gezogen
    setGezogen(null)
    setZiel(null)
    if (!g || !darfEinteilen) return
    try {
      if (g.art === 'karte') {
        onEinteilen([g.stepId], { userId, startsOn: datum, endsOn: datum })
        return
      }
      if (g.art === 'rand') {
        if (userId !== g.userId || datum < g.startsOn) return
        await updateAssignment(g.assignmentId, { endsOn: datum })
      } else {
        // Der gegriffene Tag wandert auf den Zieltag — nicht der Beginn der Einteilung.
        const versatz = Math.round((new Date(`${datum}T00:00:00Z`).getTime() - new Date(`${g.vonTag}T00:00:00Z`).getTime()) / 86_400_000)
        const neu = { startsOn: plusTage(g.startsOn, versatz), endsOn: plusTage(g.endsOn, versatz) }
        if (userId !== g.userId) {
          const name = woche?.rows.find((r) => r.userId === userId)?.name ?? ''
          if (!(await bestaetige(`Einteilung an ${name} übergeben?`))) return
          await updateAssignment(g.assignmentId, { ...neu, userId })
        } else {
          if (versatz === 0) return
          await updateAssignment(g.assignmentId, neu)
        }
      }
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const kachel = (k: WeekTile, userId: string, zellTag: string) => (
    <div
      key={k.assignmentId}
      draggable={darfEinteilen && k.visible}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move'
        setGezogen({ art: 'kachel', assignmentId: k.assignmentId, userId, startsOn: k.startsOn, endsOn: k.endsOn, vonTag: zellTag })
      }}
      onDragEnd={() => setGezogen(null)}
      onClick={() => k.stepId && onOpenStep(k.stepId)}
      className={`relative cursor-pointer rounded border-l-4 bg-card px-1.5 py-0.5 text-[11px] leading-tight shadow-sm ${k.dimmed ? 'opacity-45' : ''} ${k.done ? 'line-through opacity-60' : ''}`}
      style={{ borderLeftColor: k.phaseColor ?? 'hsl(var(--border))' }}
      title={k.visible ? `${k.projectNumber} · ${k.positionLabel} · ${k.stepLabel}${k.note ? ` · ${k.note}` : ''}` : 'belegt'}
    >
      {k.visible ? (
        <>
          <div className="flex items-center gap-0.5 font-medium">
            {k.isLead && <Star className="h-2.5 w-2.5" aria-label="führt" />}
            <span className="truncate">{k.projectNumber}</span>
            <span className="ml-auto tabular-nums text-muted-foreground">{formatStunden(k.hours)}</span>
          </div>
          <div className="truncate text-muted-foreground">{k.stepLabel}</div>
        </>
      ) : (
        <div className="italic text-muted-foreground">belegt · {formatStunden(k.hours)} h</div>
      )}
      {darfEinteilen && k.visible && (
        <span
          draggable
          onDragStart={(e) => {
            e.stopPropagation()
            setGezogen({ art: 'rand', assignmentId: k.assignmentId, userId, startsOn: k.startsOn, endsOn: k.endsOn, vonTag: zellTag })
          }}
          onClick={(e) => e.stopPropagation()}
          className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize rounded-r bg-primary/30"
          title="bis hierher verlängern: auf einen späteren Tag ziehen"
        />
      )}
    </div>
  )

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="rounded border border-border p-1 hover:bg-muted" onClick={() => setTag(plusTage(tag, -7))} aria-label="vorige Woche">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-[10rem] text-center text-sm font-medium">
          {woche ? `KW ${woche.week}/${woche.year} · ${kurzDatum(woche.from)}–${kurzDatum(woche.to)}` : '…'}
        </span>
        <button type="button" className="rounded border border-border p-1 hover:bg-muted" onClick={() => setTag(plusTage(tag, 7))} aria-label="nächste Woche">
          <ChevronRight className="h-4 w-4" />
        </button>
        <button type="button" className="rounded border border-border px-2 py-1 text-xs hover:bg-muted" onClick={() => setTag(heuteIso())}>
          heute
        </button>
        <input type="date" className="rounded border border-border bg-background px-1 py-0.5 text-xs" value={tag} onChange={(e) => e.target.value && setTag(e.target.value)} />
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" checked={nurPhase} onChange={(e) => setNurPhase(e.target.checked)} /> nur diese Phase
        </label>
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" checked={wochenende} onChange={(e) => setWochenende(e.target.checked)} /> Wochenende
        </label>
        <div className="ml-auto flex items-center gap-2">
          <select className="rounded border border-border bg-background px-1 py-0.5 text-xs" value={aushang} onChange={(e) => setAushang(e.target.value)} aria-label="Aushang für">
            <option value="">Aushang: alle</option>
            {woche?.rows.map((r) => (
              <option key={r.userId} value={r.userId}>
                Aushang: {r.name}
              </option>
            ))}
          </select>
          <ExportButton
            options={[
              { format: 'pdf', labelKey: 'export.pdf' },
              { format: 'xlsx', labelKey: 'export.xlsx' },
            ]}
            request={(format, orientation) =>
              weekExportRequest({ from: tag, phaseKey, onlyPhase: nurPhase || undefined, userId: aushang || undefined, format, orientation: orientation ?? 'landscape' })
            }
            mail={{
              source: 'EXPORT_WEEK_ASSIGNMENT',
              title: t('mailExport.titles.EXPORT_WEEK_ASSIGNMENT'),
              subject: `${t('mailExport.titles.EXPORT_WEEK_ASSIGNMENT')} KW ${woche?.week ?? ''}/${woche?.year ?? ''}`,
            }}
          />
        </div>
      </div>

      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}

      {woche && (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full table-fixed text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="w-44 px-3 py-1.5 text-left font-medium">Mitarbeiter</th>
                {tage.map((d) => (
                  <th key={d.date} className={`px-2 py-1.5 text-left font-medium ${d.date === heuteIso() ? 'text-primary' : ''}`}>
                    {d.label}
                  </th>
                ))}
                <th className="w-20 px-3 py-1.5 text-right font-medium">Σ</th>
              </tr>
            </thead>
            <tbody>
              {woche.rows.length === 0 && (
                <tr>
                  <td colSpan={tage.length + 2} className="px-4 py-8 text-center text-muted-foreground">
                    Niemand eingeteilt.
                  </td>
                </tr>
              )}
              {woche.rows.map((r) => (
                <tr key={r.userId} className="border-b border-border align-top last:border-0">
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1 font-medium">
                      {r.conflict && <AlertTriangle className="h-3.5 w-3.5 text-destructive" aria-label="Konflikt" />}
                      <span className="truncate">{r.name}</span>
                    </div>
                    {r.qualification && <div className="text-xs text-muted-foreground">{r.qualification}</div>}
                  </td>
                  {tage.map((d) => {
                    const zelle = r.days.find((c) => c.date === d.date)!
                    const key = `${r.userId}|${d.date}`
                    return (
                      <td
                        key={d.date}
                        className={`h-16 border-l border-border px-1 py-1 ${zelle.conflict ? 'bg-red-50 dark:bg-red-950/40' : zelle.warn ? 'bg-yellow-50 dark:bg-yellow-950/30' : ''} ${
                          ziel === key ? 'outline outline-2 outline-primary' : ''
                        } ${d.weekday >= 5 ? 'bg-muted/30' : ''}`}
                        onDragOver={(e) => {
                          if (!gezogen) return
                          e.preventDefault()
                          setZiel(key)
                        }}
                        onDragLeave={() => setZiel((z) => (z === key ? null : z))}
                        onDrop={(e) => {
                          e.preventDefault()
                          void ablegen(r.userId, d.date)
                        }}
                      >
                        <div className="space-y-1">
                          {zelle.absences.map((a, i) => (
                            <div key={i} className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                              {a.type}
                            </div>
                          ))}
                          {zelle.tiles.map((k) => kachel(k, r.userId, d.date))}
                          {zelle.hours > 0 && (
                            <div className={`text-right text-[10px] tabular-nums ${zelle.warn ? 'font-semibold text-yellow-700 dark:text-yellow-300' : 'text-muted-foreground'}`}>
                              {formatStunden(zelle.hours)} h
                            </div>
                          )}
                        </div>
                      </td>
                    )
                  })}
                  <td className={`px-3 py-1.5 text-right tabular-nums ${r.weekWarn ? 'font-semibold text-yellow-700 dark:text-yellow-300' : ''}`}>{formatStunden(r.weekHours)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-border bg-muted/50 font-medium">
              <tr>
                <td className="px-3 py-1.5 text-muted-foreground">Summe</td>
                {tage.map((d) => (
                  <td key={d.date} className="px-2 py-1.5 tabular-nums">
                    {formatStunden(woche.dayTotals[woche.days.findIndex((x) => x.date === d.date)])}
                  </td>
                ))}
                <td className="px-3 py-1.5 text-right tabular-nums">{formatStunden(woche.rows.reduce((s, r) => s + r.weekHours, 0))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {woche && woche.unassigned.length > 0 && (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-2">
          <div className="mb-1 text-xs font-medium text-muted-foreground">
            Nicht eingeteilt · fällig in dieser Woche · {woche.unassigned.length}
            {darfEinteilen && ' — in eine Zelle ziehen'}
          </div>
          <div className="flex flex-wrap gap-2">
            {woche.unassigned.map((k) => (
              <div
                key={k.stepId}
                draggable={darfEinteilen}
                onDragStart={() => setGezogen({ art: 'karte', stepId: k.stepId })}
                onDragEnd={() => setGezogen(null)}
                onClick={() => onOpenStep(k.stepId)}
                className="w-56 cursor-grab rounded border border-border bg-card px-2 py-1 text-xs shadow-sm"
              >
                <div className="truncate text-muted-foreground">
                  {k.project.projectNumber} · {k.position.number} {k.position.name}
                </div>
                <div className="truncate font-medium">
                  <span className="font-mono">{k.number}</span> {k.activityName}
                </div>
                <div className="text-muted-foreground">
                  {formatStunden(k.plannedHoursTotal)} h · fällig {kurzDatum(k.dueAt)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
